import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { CTAButton } from "@/components/common/CTA";
import { waLink } from "@/config/site";
import { useSettings } from "@/context/SettingsContext";

const footerNav = {
  Services: [
    { label: "Sourcing Services", path: "/sourcing-services" },
    { label: "Trading Services", path: "/trading-services" },
    { label: "Logistics Coordination", path: "/logistics-coordination" },
    { label: "Freight Rate Enquiry", path: "/rate-quote" },
    { label: "Track & Schedules", path: "/track" },
  ],
  Company: [
    { label: "About Us", path: "/about" },
    { label: "Markets We Serve", path: "/markets" },
    { label: "Industries", path: "/industries" },
    { label: "How It Works", path: "/how-it-works" },
    { label: "Insights", path: "/insights" },
  ],
  Legal: [
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms of Use", path: "/terms-of-use" },
    { label: "Cookie Notice", path: "/cookie-notice" },
    { label: "Disclaimer", path: "/disclaimer" },
  ],
};

export default function Footer() {
  const { company, contact, socials } = useSettings();
  return (
    <footer className="bg-navy text-white" data-testid="site-footer">
      {/* Final CTA band */}
      <div className="border-b border-white/10">
        <div className="container-page py-16 lg:py-20 grid gap-8 lg:grid-cols-12 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-heading font-bold tracking-tight text-[30px] leading-[36px] md:text-[38px] md:leading-[44px]">
              Your next supplier search starts with a clear requirement.
            </h2>
            <p className="mt-4 text-white/70 text-[17px] max-w-2xl">
              Send us the product details, quantity and destination. Our team will review the
              request and explain the next information or action required.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
            <CTAButton to="/request-a-quote" variant="primary" data-testid="footer-request-quote">
              Submit your requirement
            </CTAButton>
            <CTAButton href={waLink()} target="_blank" rel="noopener noreferrer" variant="ghostLight" icon={MessageCircle} data-testid="footer-whatsapp">
              Ask on WhatsApp
            </CTAButton>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="container-page py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo variant="white" />
          <p className="mt-5 text-white/60 text-[14px] leading-relaxed max-w-sm">
            {company.legalName}. An international team coordinating product sourcing,
            international trading and logistics planning across selected markets.
          </p>
          <ul className="mt-6 space-y-3 text-[14px] text-white/75">
            <li className="flex gap-3"><MapPin className="h-4 w-4 text-copper shrink-0 mt-0.5" /><span>{(contact.locations || []).join("  •  ")}</span></li>
            <li className="flex gap-3"><Phone className="h-4 w-4 text-copper shrink-0 mt-0.5" /><a href={contact.phoneHref} className="hover:text-white" data-testid="footer-phone">{contact.phone}</a></li>
            <li className="flex gap-3"><Mail className="h-4 w-4 text-copper shrink-0 mt-0.5" /><a href={`mailto:${contact.email}`} className="hover:text-white" data-testid="footer-email">{contact.email}</a></li>
            <li className="flex gap-3"><Clock className="h-4 w-4 text-copper shrink-0 mt-0.5" /><span>{contact.openingHours}</span></li>
          </ul>
        </div>

        {Object.entries(footerNav).map(([group, links]) => (
          <div key={group} className="lg:col-span-2">
            <h3 className="font-heading font-semibold text-[13px] uppercase tracking-[0.14em] text-white/50 mb-4">{group}</h3>
            <ul className="space-y-2.5">
              {links.map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-[14px] text-white/75 hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="lg:col-span-2">
          <h3 className="font-heading font-semibold text-[13px] uppercase tracking-[0.14em] text-white/50 mb-4">Connect</h3>
          <ul className="space-y-2.5">
            {(socials || []).map((s) => (
              <li key={s.label}>
                <a href={s.href} className="text-[14px] text-white/75 hover:text-white transition-colors inline-flex items-center gap-1">
                  {s.label} <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
            ))}
            <li>
              <Link to="/contact" className="text-[14px] text-white/75 hover:text-white transition-colors inline-flex items-center gap-1">
                Contact <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-page py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12.5px] text-white/50">
          <p>© {new Date().getFullYear()} {company.legalName}. All rights reserved.</p>
          <p>Company registration: {company.registration}</p>
        </div>
      </div>
    </footer>
  );
}
